/**
 * Canvas geometry for the staged-image editor.
 *
 * Separate from the component because none of it is about React: an arrow head
 * has one correct shape whichever shell draws it, and a pointer-to-canvas
 * mapping that is wrong is wrong identically everywhere.
 *
 * Adapted from the same helpers in Visual Analyzer, which is where this editor
 * comes from.
 */

/** Decode a File into an <img>. The caller revokes the url once it is drawn. */
export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => resolve({ img, url })
    img.onerror = (err) => {
      URL.revokeObjectURL(url)
      reject(err)
    }
    img.src = url
  })
}

/**
 * Scale down to fit `maxEdge` on the longest side; never scale up.
 *
 * A phone photo is 4000px on its long edge and every canvas operation on it is
 * slow enough to feel. Nobody is inspecting a screenshot at native resolution
 * inside a chat composer, and a small image left alone keeps its sharpness.
 */
export function fitWorkingSize(width, height, maxEdge) {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width: Math.round(width), height: Math.round(height) }
  const scale = maxEdge / longest
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/**
 * An export type that survives a canvas round trip.
 *
 * JPEG and WebP keep their own format because re-encoding them as PNG triples
 * the upload for no gain. Everything else becomes PNG, so transparency and the
 * hard edges of drawn lines and text stay crisp.
 */
export function exportMimeFor(originalType) {
  if (originalType === "image/jpeg" || originalType === "image/webp") return originalType
  return "image/png"
}

export function canvasToFile(canvas, mimeType, fileName) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("The edited image could not be encoded"))
        resolve(new File([blob], fileName, { type: mimeType, lastModified: Date.now() }))
      },
      mimeType,
      0.92,
    )
  })
}

/**
 * A pointer position in canvas coordinates.
 *
 * The canvas is displayed at whatever size the layout gives it, which is almost
 * never its bitmap size — so a click at CSS (x, y) has to be scaled by the ratio
 * between the two, or every mark lands offset from the cursor by a distance that
 * grows with the image.
 */
export function getCanvasPoint(canvas, clientX, clientY) {
  const rect = canvas.getBoundingClientRect()
  return {
    x: (clientX - rect.left) * (canvas.width / rect.width),
    y: (clientY - rect.top) * (canvas.height / rect.height),
  }
}

/** A line with a filled head, sized off the stroke so it stays in proportion. */
export function drawArrow(ctx, start, end, width) {
  const headLength = Math.max(12, width * 3.2)
  const angle = Math.atan2(end.y - start.y, end.x - start.x)
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(end.x, end.y)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(end.x, end.y)
  ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6))
  ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6))
  ctx.closePath()
  ctx.fillStyle = ctx.strokeStyle
  ctx.fill()
}

/** Rectangle, arrow or filled redaction between two drag points. */
export function drawShape(ctx, tool, start, end, color, width) {
  ctx.save()
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = width
  ctx.lineCap = "round"
  ctx.lineJoin = "round"

  const x = Math.min(start.x, end.x)
  const y = Math.min(start.y, end.y)
  const w = Math.abs(end.x - start.x)
  const h = Math.abs(end.y - start.y)

  if (tool === "rect") ctx.strokeRect(x, y, w, h)
  else if (tool === "redact") {
    /* Filled, opaque, and drawn onto the bitmap itself — not an overlay.
       A redaction that is a layer on top is a redaction somebody can remove,
       and the whole point is that what it covers never leaves this machine. */
    ctx.fillStyle = "#10230a"
    ctx.fillRect(x, y, w, h)
  } else if (tool === "arrow") drawArrow(ctx, start, end, width)

  ctx.restore()
}

/** Rotate a canvas a quarter turn, returning a new one. */
export function rotateCanvas(source, quarterTurns = 1) {
  const turns = ((quarterTurns % 4) + 4) % 4
  if (turns === 0) return source
  const swapped = turns % 2 === 1
  const out = document.createElement("canvas")
  out.width = swapped ? source.height : source.width
  out.height = swapped ? source.width : source.height
  const ctx = out.getContext("2d")
  ctx.translate(out.width / 2, out.height / 2)
  ctx.rotate((turns * Math.PI) / 2)
  ctx.drawImage(source, -source.width / 2, -source.height / 2)
  return out
}

/** Cut a rectangle out of a canvas, returning a new one. */
export function cropCanvas(source, rect) {
  const x = Math.max(0, Math.round(Math.min(rect.start.x, rect.end.x)))
  const y = Math.max(0, Math.round(Math.min(rect.start.y, rect.end.y)))
  const width = Math.min(source.width - x, Math.round(Math.abs(rect.end.x - rect.start.x)))
  const height = Math.min(source.height - y, Math.round(Math.abs(rect.end.y - rect.start.y)))
  if (width < 8 || height < 8) return source

  const out = document.createElement("canvas")
  out.width = width
  out.height = height
  out.getContext("2d").drawImage(source, x, y, width, height, 0, 0, width, height)
  return out
}
